/**
 * CSV serialisation for "export what I am looking at" buttons.
 *
 * Lifted out of `apps/admin-ui/src/pages/billing/ledger/index.tsx`, where it
 * was a private function, so the tenant console's ledger export does not carry
 * a second copy of the same escaping rules.
 */

export interface RowsToCsvOptions {
  /**
   * Column order. Defaults to the keys of the first row, which is only
   * correct when every row has the same shape.
   */
  headers?: string[];
  /** Emit a UTF-8 BOM. Excel reads a plain UTF-8 CSV as Latin-1 without it. */
  bom?: boolean;
}

/**
 * RFC 4180 quoting: a field containing a quote, comma or newline is wrapped in
 * quotes and its own quotes are doubled.
 *
 * A leading `=`, `@`, or a `+`/`-` not followed by a plain number, is prefixed
 * with an apostrophe: without it a cell reading `=cmd|…` is a formula that
 * Excel offers to execute when the file is opened. The number carve-out
 * matters because a phone number legitimately starts with `+`.
 */
function escapeCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  let s = String(value);

  const first = s[0];
  const risky = first === '=' || first === '@'
    || ((first === '+' || first === '-') && !/^[+-][\d\s()-]+$/.test(s));
  if (risky) s = `'${s}`;

  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Serialises an array of flat records as CSV. Returns `''` for no rows. */
export function rowsToCsv(
  rows: ReadonlyArray<Record<string, unknown>>,
  opts: RowsToCsvOptions = {}
): string {
  if (rows.length === 0) return '';
  const headers = opts.headers ?? Object.keys(rows[0]);
  const lines = [headers.map(escapeCell).join(',')];
  for (const row of rows) lines.push(headers.map((h) => escapeCell(row[h])).join(','));
  return (opts.bom ? '﻿' : '') + lines.join('\n');
}
